import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getWsUrl } from '../../utils/api-url';
import { gameAPI } from '../../services/api';
import { useGameStore } from '../../store/gameStore';
import PhoneAuth from '../auth/PhoneAuth';

interface WorldGeneratedScreenProps {
  gameId: string;
}

export default function WorldGeneratedScreen({ gameId }: WorldGeneratedScreenProps) {
  const { t } = useTranslation();
  const [isStarting, setIsStarting] = useState(false);
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  const [savedToAccount, setSavedToAccount] = useState(false);
  const [introReady, setIntroReady] = useState(false);
  const { players } = useGameStore();

  // Check if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Initialize from localStorage immediately
    const userData = localStorage.getItem('userData');
    console.log('🔍 Checking auth status:', userData ? 'logged in' : 'not logged in');
    return !!userData;
  });

  useEffect(() => {
    const checkAuthStatus = () => {
      const userData = localStorage.getItem('userData');
      const isUserLoggedIn = !!userData;
      console.log('🔍 Auth check:', { userData, isUserLoggedIn });

      setIsLoggedIn(isUserLoggedIn);
      if (isUserLoggedIn) {
        setSavedToAccount(true); // Auto-saved if logged in
      }
    };

    // Check on mount
    checkAuthStatus();

    // Listen for storage changes (logout in another tab/component)
    window.addEventListener('storage', checkAuthStatus);

    // Custom event for same-window logout
    window.addEventListener('userLogout', checkAuthStatus);

    return () => {
      window.removeEventListener('storage', checkAuthStatus);
      window.removeEventListener('userLogout', checkAuthStatus);
    };
  }, []);

  // Listen for intro ready event
  useEffect(() => {
    let socket: any = null;

    const handleIntroReady = (data: { narrative: string; imageId: string }) => {
      console.log('✅ Intro ready, redirecting to intro screen...');
      setIntroReady(true);

      // Wait a moment to show the "Ready!" state, then redirect
      setTimeout(() => {
        const playerId = localStorage.getItem(`game_${gameId}_player`);
        const url = playerId ? `/intro/${gameId}?playerId=${playerId}` : `/intro/${gameId}`;
        window.location.href = url;
      }, 1500);
    };

    // Import socket.io-client and listen for events
    import('socket.io-client').then(({ io }) => {
      const wsUrl = getWsUrl();
      socket = io(wsUrl);

      socket.emit('game:join', { gameId });
      socket.on('game:intro:ready', handleIntroReady);
    });

    return () => {
      if (socket) {
        socket.off('game:intro:ready', handleIntroReady);
        socket.disconnect();
      }
    };
  }, [gameId]);

  const handleManualStart = async () => {
    // Always allow starting - intro page will handle waiting if needed
    setIsStarting(true);
    const playerId = localStorage.getItem(`game_${gameId}_player`);
    const url = playerId ? `/intro/${gameId}?playerId=${playerId}` : `/intro/${gameId}`;
    window.location.href = url;
  };

  const handlePhoneAuthSuccess = (userData: any) => {
    console.log('✅ Game saved to account:', userData);
    setSavedToAccount(true);
    setIsLoggedIn(true); // Update login status
    setShowPhoneAuth(false);

    // Store user data in localStorage (same format as App.tsx)
    localStorage.setItem('userData', JSON.stringify(userData));
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
      <div style={{
        padding: '3rem 2rem',
        background: 'rgba(15, 12, 41, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        animation: 'fadeIn 0.5s ease-out',
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🎉</div>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#e2e8f0' }}>
          {t('worldGenerated.title')}
        </h2>
        <p style={{ color: '#a0aec0', marginBottom: '2rem', fontSize: '1.1rem' }}>
          {t('worldGenerated.subtitle')}
        </p>

        {/* Player List */}
        <div style={{
          padding: '1.5rem',
          background: 'rgba(99, 102, 241, 0.1)',
          borderRadius: '16px',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          marginBottom: '2rem',
        }}>
          <p style={{ fontSize: '1rem', color: '#cbd5e0', marginBottom: '1rem', fontWeight: 'bold' }}>
            👥 {t('worldGenerated.playersCount', { count: players.length })}:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {players.map((player, index) => (
              <div
                key={player.id}
                style={{
                  padding: '0.75rem',
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '10px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: '#e2e8f0', fontWeight: '600' }}>
                    {index + 1}. {player.name}
                  </div>
                  {player.role && (
                    <div style={{ fontSize: '0.85rem', color: '#a0aec0' }}>
                      {player.role}
                    </div>
                  )}
                </div>
                {player.avatar_data && (
                  <div style={{
                    padding: '0.25rem 0.5rem',
                    background: 'rgba(16, 185, 129, 0.2)',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    color: '#10b981',
                  }}>
                    {t('worldGenerated.hasAvatar')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Save Game Button - only show if not logged in and not saved */}
        {!isLoggedIn && !savedToAccount && (
          <button
            onClick={() => setShowPhoneAuth(true)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '1rem 1.5rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              border: '1px solid rgba(16, 185, 129, 0.5)',
              borderRadius: '16px',
              background: 'rgba(16, 185, 129, 0.1)',
              color: '#10b981',
              cursor: 'pointer',
              transition: 'all 0.3s',
              marginBottom: '1rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {t('worldGenerated.saveToAccount')}
          </button>
        )}

        {/* Show saved status only if user just saved via button (not if already logged in) */}
        {!isLoggedIn && savedToAccount && (
          <div style={{
            padding: '1rem',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px',
            marginBottom: '1rem',
          }}>
            <p style={{ color: '#10b981', margin: 0, fontSize: '0.9rem' }}>
              ✅ {t('worldGenerated.gameSaved')}
            </p>
          </div>
        )}

        {/* Start Game Button - always show */}
        <button
          onClick={handleManualStart}
          disabled={isStarting}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '1.5rem 2rem',
            fontSize: '1.3rem',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '16px',
            background: isStarting
              ? 'rgba(99, 102, 241, 0.3)'
              : introReady
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            cursor: isStarting ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s',
            boxShadow: introReady
              ? '0 10px 30px rgba(16, 185, 129, 0.4)'
              : '0 10px 30px rgba(99, 102, 241, 0.4)',
            animation: introReady ? 'pulse 2s infinite' : 'none',
          }}
          onMouseEnter={(e) => {
            if (!isStarting) {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = introReady
                ? '0 15px 40px rgba(16, 185, 129, 0.6)'
                : '0 15px 40px rgba(99, 102, 241, 0.6)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = introReady
              ? '0 10px 30px rgba(16, 185, 129, 0.4)'
              : '0 10px 30px rgba(99, 102, 241, 0.4)';
          }}
        >
          {isStarting ? '⏳ ' + t('common.loading') : (introReady ? t('worldGenerated.introReady') : t('worldGenerated.startGame'))}
        </button>

        {players.length > 1 && (
          <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '1.5rem' }}>
            {t('worldGenerated.multiplayerInfo')}
          </p>
        )}

        {/* Phone Auth Modal */}
        {showPhoneAuth && (
          <PhoneAuth
            onSuccess={handlePhoneAuthSuccess}
            onCancel={() => setShowPhoneAuth(false)}
            gameId={gameId}
          />
        )}

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.9; }
          }
        `}</style>
      </div>
    </div>
  );
}
